import { NOT_AVAILABLE } from '@constants/common';
import { withTooltip } from '@hooks/common/useDynamicColumns/utils/withTooltip';
import { PropertyLeadEntity } from '@hooks/types';
import { formatCurrency } from '@utils/formatCurrency';
import { capitalize } from '@utils/formatInitials';
import { get } from 'lodash-es';

import LockOpenIcon from '@mui/icons-material/LockOpen';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Box, Button, Tooltip, Typography } from '@mui/material';

export const createPropertyColumns = () => [
  {
    field: 'unlock_lead',
    headerName: '',
    headerClassName: 'super-bold--header',
    width: 110,
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({
      row,
    }: {
      row: PropertyLeadEntity & { purchased?: boolean; is_purchased?: boolean; _isPreviewRow?: boolean };
    }) => {
      const isPurchased = row.purchased === true || row.is_purchased === true;
      const isPreviewRow = row._isPreviewRow === true;

      if (isPurchased) {
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <LockOpenIcon sx={{ color: '#00d4aa', fontSize: 16 }} />
            <Typography variant="caption" sx={{ color: '#00d4aa', fontWeight: 600, fontSize: '0.7rem' }}>
              Unlocked
            </Typography>
          </Box>
        );
      }

      if (isPreviewRow) {
        return (
          <Tooltip title="Free preview row - purchase to save to your leads">
            <Typography variant="caption" sx={{ color: '#90caf9', fontWeight: 500, fontSize: '0.7rem' }}>
              Preview
            </Typography>
          </Tooltip>
        );
      }

      return (
        <Tooltip title="Purchase lead for 1 credit to reveal contact info">
          <Button
            size="small"
            variant="contained"
            onClick={e => e.stopPropagation()}
            sx={{
              backgroundColor: 'rgba(0, 212, 170, 0.15)',
              color: '#00d4aa',
              fontSize: '0.65rem',
              fontWeight: 600,
              textTransform: 'none',
              minWidth: 'auto',
              px: 1,
              py: 0.25,
              borderRadius: '4px',
              '&:hover': { backgroundColor: 'rgba(0, 212, 170, 0.25)' },
            }}
            startIcon={<ShoppingCartIcon sx={{ fontSize: '12px !important' }} />}
          >
            1 credit
          </Button>
        </Tooltip>
      );
    },
  },
  {
    field: 'address',
    headerName: 'Address',
    headerClassName: 'super-bold--header',
    width: 300,
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: PropertyLeadEntity }) => withTooltip(get(row, 'data.address', NOT_AVAILABLE)),
  },
  {
    field: 'price',
    headerName: 'Price',
    headerClassName: 'super-bold--header',
    width: 150,
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: PropertyLeadEntity }) => {
      const result = get(row, 'data.price');
      return withTooltip(formatCurrency(result as unknown as number) || NOT_AVAILABLE);
    },
  },
  {
    field: 'property_type',
    headerName: 'Property Type',
    headerClassName: 'super-bold--header',
    width: 150,
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: PropertyLeadEntity }) =>
      withTooltip(capitalize(get(row, 'data.property_type')).replaceAll('_', ' ') || NOT_AVAILABLE),
  },
  {
    field: 'bedrooms',
    headerName: 'Bedrooms',
    headerClassName: 'super-bold--header',
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: PropertyLeadEntity }) => withTooltip(get(row, 'data.bedrooms', NOT_AVAILABLE)),
  },
  {
    field: 'bathrooms',
    headerName: 'Bathrooms',
    headerClassName: 'super-bold--header',
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: PropertyLeadEntity }) => withTooltip(get(row, 'data.bathrooms', NOT_AVAILABLE)),
  },
  {
    field: 'size',
    headerName: 'Size, sqft',
    headerClassName: 'super-bold--header',
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: PropertyLeadEntity }) => withTooltip(get(row, 'data.size', NOT_AVAILABLE)),
  },
  {
    field: 'status',
    headerName: 'Status',
    headerClassName: 'super-bold--header',
    hideable: false,
    width: 150,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: PropertyLeadEntity }) => {
      const result = get(row, 'data.status', NOT_AVAILABLE);
      return withTooltip(capitalize(result).replaceAll('_', ' '));
    },
  },
  {
    field: 'contact_phone',
    headerName: 'Contact phone',
    headerClassName: 'super-bold--header',
    width: 150,
    hideable: false,
    sortable: false,
    resizable: false,
    renderCell: ({ row }: { row: PropertyLeadEntity }) => withTooltip(get(row, 'data.contact_phone', NOT_AVAILABLE)),
  },
];
